import { ReactElement, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { Box, Button, Card, Typography } from '@mui/material'

import Loading from 'src/v3/components/loading'
import { EventQuiz } from 'src/v3/events/components/event-quiz'
import { ConfirmIcon } from 'src/v3/core/icons'
import {
  fetchQuiz,
  selectQuizByEvent,
  selectQuizStatusByEvent,
} from 'src/v3/events/events.slice'
import {
  fetchEventProgress,
  fetchEventQuizQuestions,
  fetchEvents,
  selectEventPhase,
  selectEventProgressStatus,
  selectEventQuizQuestions,
  selectEventQuizQuestionsStatus,
  selectEventV2ById,
  selectEventsStatus,
} from './events-v2.slice'
import { useFreshFetch } from './hooks/use-fresh-fetch'

let TakeQuiz = (): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let { eventId } = useParams()
  let eventID = eventId ?? ''

  let event = useAppSelector(selectEventV2ById(eventID))
  let eventsStatus = useAppSelector(selectEventsStatus)
  let quiz = useAppSelector(selectQuizByEvent(eventID))
  let quizStatus = useAppSelector(selectQuizStatusByEvent(eventID))
  let questions = useAppSelector(selectEventQuizQuestions(eventID))
  let questionsStatus = useAppSelector(selectEventQuizQuestionsStatus(eventID))
  let progressStatus = useAppSelector(selectEventProgressStatus(eventID))
  let phaseKind = useAppSelector(selectEventPhase(eventID))?.phase

  // /progress decides whether this page offers the quiz at all; the local result inside
  // EventQuiz only covers the attempt in progress, so a just-finished attempt still shows
  // its score. Nothing re-fetches progress while this page is mounted, so a pass here
  // doesn't redirect away before the user has read it.
  let progressFresh = useFreshFetch(eventID, progressStatus, fetchEventProgress)
  let canTakeQuiz = progressFresh && phaseKind === 'quiz'
  // Questions carry the session's isCurrent flags, which EventQuiz uses to pick its starting
  // question, so a cached list from a previous session (e.g. before a retake) must never
  // mount it. Fetched only once progress offers the quiz: the API rejects the read without
  // attendance in the current attempt.
  let questionsFresh = useFreshFetch(
    canTakeQuiz ? eventID : '',
    questionsStatus,
    fetchEventQuizQuestions,
  )
  let loadFailed =
    progressStatus === 'rejected' ||
    quizStatus === 'rejected' ||
    questionsStatus === 'rejected'

  // Passed, not required, attendance pending, or not eligible: the detail page shows
  // whatever comes next. replace keeps the back button from bouncing through here.
  useEffect(() => {
    if (progressFresh && phaseKind !== 'quiz') {
      navigate(`/events/v2/${eventID}`, { replace: true })
    }
  }, [progressFresh, phaseKind, eventID])

  useEffect(() => {
    if (eventsStatus === 'uninitialized') {
      dispatch(fetchEvents())
    }
  }, [dispatch, eventsStatus])

  useEffect(() => {
    if ((eventID && !quizStatus) || quizStatus === 'uninitialized') {
      dispatch(fetchQuiz(eventID))
    }
  }, [dispatch, quizStatus, eventID])

  return (
    <>
      <Box>
        {loadFailed && (
          <Typography color="error" role="alert">
            We couldn't load this quiz. Please refresh the page to try again.
          </Typography>
        )}
        {!loadFailed && canTakeQuiz && questionsFresh && (
          <EventQuiz
            key={eventID}
            name={event?.name || 'Event'}
            quiz={quiz}
            questionData={questions}
            apiPath={`/api/event/v2/${eventID}`}
            proceedButton={
              <Card variant="outlined" sx={{ p: 3, mt: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ textAlign: 'center', fontWeight: 500 }}
                  >
                    Ready to Complete Your Event?
                  </Typography>
                  <Typography
                    color="textSecondary"
                    sx={{ textAlign: 'center', maxWidth: 500 }}
                  >
                    Click the button below to finalize your event completion and
                    access your certificate.
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<ConfirmIcon />}
                    onClick={() => {
                      navigate(`/events/v2/${eventID}`, { replace: true })
                    }}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 500,
                    }}
                  >
                    Complete Event
                  </Button>
                </Box>
              </Card>
            }
          />
        )}
      </Box>
      {/* The questions fetch waits for progress to offer the quiz, so a failed progress or quiz
          fetch leaves its status undefined. Without this guard the backdrop would cover the error. */}
      <Loading
        entities={
          loadFailed
            ? []
            : [
                eventsStatus,
                quizStatus ?? 'uninitialized',
                questionsStatus ?? 'uninitialized',
                progressStatus ?? 'uninitialized',
              ]
        }
      />
    </>
  )
}

export default TakeQuiz
